import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Icon } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { i18n } from '../config';
import {
  fetchKbMarkets,
  fetchKbStructure,
  firstKbArticle,
  getKbLang,
  getKbMarket,
  KB_LANGS,
  type KbArticleRef,
  type KbLang,
  type KbMarket,
  type KbMarketInfo,
  type KbSectionNode,
} from '../lib';
import { useKbPanel } from '../model';

import { KbArticle, ArticleSkeleton } from './KbArticle';
import { KbTree, KbTreeSkeleton } from './KbTree';
import styles from './KnowledgeBase.module.scss';

type StructureStatus = 'idle' | 'loading' | 'ready' | 'error';

// Native names for the KB content languages.
const KB_LANG_LABELS: Record<KbLang, string> = {
  en: 'English',
  ru: 'Русский',
  es: 'Español',
  de: 'Deutsch',
};

// Full market names for the small header label (fallback until the hub's
// market directory loads).
const MARKET_LABELS: Record<KbMarket, string> = {
  global: 'Global',
  americas: 'Americas',
  'us-fda': 'United States',
  cis: 'CIS',
};

// The language the panel requests: the explicit override, else the user's
// locale when the market serves it, else the market's default.
const pickKbLang = (
  userLang: KbLang | undefined,
  info: KbMarketInfo | null,
): KbLang | undefined => {
  if (!info) return userLang;
  if (userLang && info.languages.includes(userLang)) return userLang;

  return (KB_LANGS as readonly string[]).includes(info.defaultLanguage)
    ? (info.defaultLanguage as KbLang)
    : undefined;
};

type KbSelection = {
  slug: string;
  // The market the article is fetched from; internal hub links may point
  // at a market other than the one the panel tree was built for.
  market: string;
};

// The panel state (open + selected article) lives in the URL fragment so a
// refreshed or shared page reopens the same spot: #kb/<market>/<slug>.
const readKbHash = (): KbSelection | null => {
  const match = /^#kb\/([^/]+)\/(.+)$/.exec(window.location.hash);

  return match
    ? {
        market: decodeURIComponent(match[1]),
        slug: decodeURIComponent(match[2]),
      }
    : null;
};

const writeKbHash = (selection: KbSelection | null): void => {
  const hash = selection
    ? `#kb/${encodeURIComponent(selection.market)}/${encodeURIComponent(selection.slug)}`
    : '';

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${hash}`,
  );
};

export const KnowledgeBase = () => {
  const { isOpen, slug, close } = useKbPanel();
  const intl = useIntl();
  const { isFDA, isFDA3DGPRestrictions } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );
  const locale = useAppSelector(userModel.selectors.selectUserLocale);
  const market = getKbMarket(isFDA || isFDA3DGPRestrictions);
  // An explicit KB content language picked in the header; null follows the
  // user's locale (with the hub's market-default fallback).
  const [langOverride, setLangOverride] = useState<KbLang | null>(null);
  // The hub's market directory entry: full label + the content languages
  // this market serves.
  const [marketInfo, setMarketInfo] = useState<KbMarketInfo | null>(null);
  const effectiveLang =
    langOverride ?? pickKbLang(getKbLang(locale), marketInfo);

  const structureKey = market ? `${market}:${effectiveLang ?? ''}` : null;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [structureStatus, setStructureStatus] =
    useState<StructureStatus>('idle');
  const [sections, setSections] = useState<KbSectionNode[]>([]);
  const [rootArticles, setRootArticles] = useState<KbArticleRef[]>([]);
  const [selection, setSelection] = useState<KbSelection | null>(null);
  // Below the 860px breakpoint the nav renders as a slide-over drawer;
  // this state only affects that drawer (desktop ignores it).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const panelRef = useRef<HTMLElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const lastStructureKeyRef = useRef<string | null>(structureKey);

  // A slug passed to openKnowledgeBase() preselects an article; otherwise
  // the URL fragment restores the last viewed spot.
  useEffect(() => {
    if (!isOpen || !market) return;

    if (slug) {
      setSelection({ slug, market });
      return;
    }

    const fromHash = readKbHash();

    if (fromHash) setSelection(fromHash);
  }, [isOpen, slug, market]);

  // A selection carried over from another market or language is dropped:
  // its slug may not exist in the structure now shown.
  useEffect(() => {
    if (lastStructureKeyRef.current !== structureKey) {
      lastStructureKeyRef.current = structureKey;
      setSelection(null);
    }
  }, [structureKey]);

  useEffect(() => {
    if (!isOpen || !market || !structureKey || structureKey === loadedKey) {
      return;
    }

    let active = true;
    setStructureStatus('loading');
    setSections([]);

    fetchKbStructure(market, getKbLang(locale))
      .then((structure) => {
        if (!active) return;
        setSections(structure.sections);
        setRootArticles(structure.rootArticles);
        setStructureStatus('ready');
        setLoadedKey(structureKey);
        // Nothing restored or preselected: open the first page in order.
        setSelection((current) => {
          if (current) return current;

          const first = firstKbArticle(structure);

          return first ? { slug: first.slug, market } : null;
        });
      })
      .catch(() => {
        if (!active) return;
        setStructureStatus('error');
        setLoadedKey(structureKey);
      });

    return () => {
      active = false;
    };
  }, [isOpen, structureKey, loadedKey, market, locale]);

  // The market directory is static per market; fetched once per open.
  useEffect(() => {
    if (!isOpen || !market) return;

    let active = true;

    fetchKbMarkets()
      .then((markets) => {
        if (!active) return;
        setMarketInfo(markets.find((entry) => entry.code === market) ?? null);
      })
      .catch(() => {
        if (!active) return;
        setMarketInfo(null);
      });

    return () => {
      active = false;
    };
  }, [isOpen, market]);

  // Selection (and open state) mirror into the URL fragment; closing clears it.
  useEffect(() => {
    writeKbHash(isOpen ? selection : null);
  }, [isOpen, selection]);

  useEffect(() => {
    if (!isOpen) {
      setMobileNavOpen(false);
      setLangOverride(null);
      setMarketInfo(null);
      return;
    }

    trackEvent({ eventName: 'KnowledgeBasePanelOpened' });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // Focus moves into the dialog on open and returns to the trigger on close.
  useEffect(() => {
    if (!isOpen) return;

    lastFocusedElementRef.current = document.activeElement as HTMLElement;

    panelRef.current?.focus();

    return () => lastFocusedElementRef.current?.focus();
  }, [isOpen]);

  if (!isOpen || !market) return null;

  return createPortal(
    <div
      className={styles.overlay}
      data-component="knowledge-base-overlay"
      onClick={close}
    >
      <aside
        ref={panelRef}
        className={styles.panel}
        data-component="knowledge-base-panel"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        aria-label={intl.formatMessage(i18n.title)}
      >
        <header className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>
            <FormattedMessage {...i18n.title} />
          </h2>
          <div className={styles.panelActions}>
            <span className={styles.marketLabel}>
              {marketInfo?.label ?? MARKET_LABELS[market]}
            </span>
            {(marketInfo?.languages ?? [...KB_LANGS]).length > 1 && (
              <select
                className={styles.langSelect}
                value={
                  effectiveLang &&
                  (marketInfo?.languages ?? [...KB_LANGS]).includes(
                    effectiveLang,
                  )
                    ? effectiveLang
                    : ''
                }
                onChange={(event) =>
                  setLangOverride((event.target.value || null) as KbLang | null)
                }
                aria-label={intl.formatMessage(i18n.language)}
              >
                <option value="">
                  <FormattedMessage {...i18n.autoLanguage} />
                </option>
                {(marketInfo?.languages ?? [...KB_LANGS])
                  .filter((lang) =>
                    (KB_LANGS as readonly string[]).includes(lang),
                  )
                  .map((lang) => (
                    <option key={lang} value={lang}>
                      {KB_LANG_LABELS[lang as KbLang]}
                    </option>
                  ))}
              </select>
            )}
            <button
              type="button"
              className={styles.menuButton}
              onClick={() => setMobileNavOpen((open) => !open)}
              aria-label={intl.formatMessage(i18n.menu)}
              aria-expanded={mobileNavOpen}
            >
              <Icon name="list" size={24} />
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={close}
              aria-label={intl.formatMessage(i18n.close)}
            >
              <Icon name="close" size={24} />
            </button>
          </div>
        </header>
        <div className={styles.panelBody}>
          {mobileNavOpen && (
            <div
              className={styles.treeScrim}
              onClick={() => setMobileNavOpen(false)}
            />
          )}
          <nav
            className={
              mobileNavOpen
                ? `${styles.treePane} ${styles.treePaneOpen}`
                : styles.treePane
            }
          >
            {structureStatus === 'loading' && <KbTreeSkeleton />}
            {structureStatus === 'error' && (
              <div className={styles.panePlaceholder}>
                <p>
                  <FormattedMessage {...i18n.loadError} />
                </p>
                <Button variant="secondary" onClick={() => setLoadedKey(null)}>
                  <FormattedMessage {...i18n.retry} />
                </Button>
              </div>
            )}
            {structureStatus === 'ready' && (
              <KbTree
                sections={sections}
                rootArticles={rootArticles}
                selectedSlug={selection?.slug ?? null}
                onSelect={(slug) => {
                  setSelection({ slug, market });
                  setMobileNavOpen(false);
                }}
              />
            )}
          </nav>
          <div className={styles.articlePane}>
            {/* The first page is picked only after the structure lands, so
                while it loads the pane shows the same article skeleton the
                page fetch will show. */}
            {structureStatus === 'loading' ? (
              <ArticleSkeleton />
            ) : (
              <KbArticle
                market={selection?.market ?? market}
                slug={selection?.slug ?? ''}
                locale={langOverride ?? locale}
                onOpenArticle={(slug, articleMarket) =>
                  setSelection({ slug, market: articleMarket ?? market })
                }
              />
            )}
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
